/**
 * Standard odontogram geometry (patient-right → patient-left).
 * This is screen anatomy, not a PMS write rule. Each adapter decides
 * how to use it.
 */

import type { ScribeSiteCode } from './scribe-sites';

const FACIAL_LTR_RIGHT: readonly ScribeSiteCode[] = ['DB', 'B', 'MB'];
const FACIAL_LTR_LEFT: readonly ScribeSiteCode[] = ['MB', 'B', 'DB'];
const LINGUAL_LTR_RIGHT: readonly ScribeSiteCode[] = ['DL', 'L', 'ML'];
const LINGUAL_LTR_LEFT: readonly ScribeSiteCode[] = ['ML', 'L', 'DL'];

/**
 * Lingual L→R walk-back (upper 16→1, lower 17→32).
 * Not the Pearl display trio — right-side display is DL, L, ML.
 */
const LINGUAL_WALK_RIGHT: readonly ScribeSiteCode[] = ['ML', 'L', 'DL'];
const LINGUAL_WALK_LEFT: readonly ScribeSiteCode[] = ['DL', 'L', 'ML'];

export interface OdontogramSlot {
  tooth: number;
  site: ScribeSiteCode;
}

/**
 * Teeth 1–8 and 25–32 sit on the patient's right, so screen-left is distal.
 *
 * @param toothNumber - Universal tooth number 1–32
 * @returns true on the patient's right
 */
export function isPatientRightTooth(toothNumber: number): boolean {
  return (
    (toothNumber >= 1 && toothNumber <= 8) ||
    (toothNumber >= 25 && toothNumber <= 32)
  );
}

/**
 * @param toothNumber - Universal tooth number 1–32
 * @returns true on the mandible
 */
export function isMandibularTooth(toothNumber: number): boolean {
  return toothNumber >= 17 && toothNumber <= 32;
}

/**
 * Facial sites left-to-right as Pearl draws them.
 * Patient-right is distal-first; patient-left is mesial-first.
 * Walk order can differ on lingual — do not reuse this for a walk-back.
 *
 * @param toothNumber - Universal tooth number 1–32
 * @returns Three facial site codes, screen-left to screen-right
 * @example
 * facialSitesLtr(3) // ['DB', 'B', 'MB']
 * facialSitesLtr(9) // ['MB', 'B', 'DB']
 */
export function facialSitesLtr(toothNumber: number): readonly ScribeSiteCode[] {
  return isPatientRightTooth(toothNumber) ? FACIAL_LTR_RIGHT : FACIAL_LTR_LEFT;
}

/**
 * Lingual sites left-to-right as Pearl draws them.
 * Patient-right is distal-first (DL, L, ML). That is display, not the
 * Voice lingual walk-back (ML, L, DL on the right).
 *
 * @param toothNumber - Universal tooth number 1–32
 * @returns Three lingual site codes, screen-left to screen-right
 * @example
 * lingualSitesLtr(32) // ['DL', 'L', 'ML']
 * lingualSitesLtr(17) // ['ML', 'L', 'DL']
 */
export function lingualSitesLtr(
  toothNumber: number,
): readonly ScribeSiteCode[] {
  return isPatientRightTooth(toothNumber)
    ? LINGUAL_LTR_RIGHT
    : LINGUAL_LTR_LEFT;
}

function lingualWalkSites(toothNumber: number): readonly ScribeSiteCode[] {
  return isPatientRightTooth(toothNumber)
    ? LINGUAL_WALK_RIGHT
    : LINGUAL_WALK_LEFT;
}

/**
 * Writer walk matching Voice `full_arch_mirrored` (1–16F, 16–1L, 32–17F, 17–32L).
 * Intra-tooth sites mirror at the midline (right facial DB→B→MB, left MB→B→DB;
 * lingual walk-back left DL→L→ML, right ML→L→DL).
 *
 * @returns 192 tooth+site slots in Voice walk order
 */
export function fullArchMirroredSequence(): OdontogramSlot[] {
  const seq: OdontogramSlot[] = [];
  for (let tooth = 1; tooth <= 16; tooth++) {
    for (const site of facialSitesLtr(tooth)) seq.push({ tooth, site });
  }
  for (let tooth = 16; tooth >= 1; tooth--) {
    for (const site of lingualWalkSites(tooth)) seq.push({ tooth, site });
  }
  for (let tooth = 32; tooth >= 17; tooth--) {
    for (const site of facialSitesLtr(tooth)) seq.push({ tooth, site });
  }
  for (let tooth = 17; tooth <= 32; tooth++) {
    for (const site of lingualWalkSites(tooth)) seq.push({ tooth, site });
  }
  return seq;
}
